import { cn } from '@/lib/utils';
// Custom auth replaces Cognito. We call our OTP service directly
// instead of the Amplify CUSTOM_WITHOUT_SRP challenge dance.
import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { useMemo, useState } from 'react';
import { authService } from '../services/auth.service';

type AuthMode = 'email' | 'phone';

/**
 * Handoff shape for the "OTP-verified but no Patient row matched"
 * case. The public booking flow on healthcom-schedule passes this up
 * from `EmailOrPhoneLogin` → `ScheduleConfirm` → `Schedule` so the
 * eventual `POST /appointment-guest` call can prove ownership of the
 * contact via `challengeId` and mint a pending Patient row.
 *
 * Lives here (next to the component that originates the value) so all
 * three consumers share one definition — inline copies drift.
 */
export type VerifiedGuestContact = {
  contact: string;
  contactType: AuthMode;
  challengeId: string;
};

const COMMON_COUNTRIES: CountryCode[] = ['US', 'CA', 'MX', 'GB', 'AU'];

// Render the flag emoji for an ISO-3166 alpha-2 country code by mapping
// each letter to its Regional Indicator Symbol. Works on iOS/macOS
// natively; Android falls back to the two-letter code.
const flagFor = (iso: string): string =>
  iso
    .toUpperCase()
    .split('')
    .map(c => String.fromCodePoint(0x1f1a5 + c.charCodeAt(0)))
    .join('');

const COUNTRY_OPTIONS = (() => {
  const all = getCountries();
  const common = COMMON_COUNTRIES.filter(c => all.includes(c));
  const rest = all.filter(c => !COMMON_COUNTRIES.includes(c)).sort();

  return [...common, ...rest].map(iso => ({
    iso,
    callingCode: getCountryCallingCode(iso),
    label: `${flagFor(iso)} +${getCountryCallingCode(iso)}`,
  }));
})();

// Neutral input style — works on white card backgrounds and on the
// gradient hero shell (RootLanding) since the bg is `bg-white` and the
// ring/outline use neutral grays + the primary CSS variable.
const inputClass =
  'w-full rounded-xl border-0 bg-white dark:bg-gray-950 px-3.5 py-2.5 text-sm text-gray-900 dark:text-gray-100 ring-1 ring-gray-200 dark:ring-white/10 outline-2 -outline-offset-1 outline-transparent focus:outline-[var(--primary-color)] placeholder:text-gray-400 dark:placeholder:text-gray-600 max-sm:text-base/5';

type Props = {
  /**
   * Called after `confirmSignIn` resolves successfully. The callee is
   * responsible for any follow-up (e.g. setUser, fetching /me/{path},
   * advancing a state machine, navigating, etc.). The component itself
   * doesn't navigate or touch global state — it just authenticates.
   */
  onAuthenticated: () => void | Promise<void>;
  /**
   * Public-booking hook for the "verified but no patient row matched"
   * case. When provided, a verify-success with `matches.length === 0`
   * fires this callback (carrying the verified contact + the live
   * challengeId) instead of erroring with "No matching account.". The
   * portal sign-in path leaves this unset so it keeps the "we don't
   * have an account for this contact" message.
   */
  onVerifiedWithoutMatch?: (args: VerifiedGuestContact) => void | Promise<void>;
  /**
   * Optional copy override for the submit button label. Defaults to
   * "Continue".
   */
  submitLabel?: string;
  /**
   * Optional copy override for the OTP submit button label. Defaults
   * to "Verify".
   */
  verifyLabel?: string;
  /**
   * The clinic being booked, in the public booking flow. An OTP-verified
   * contact can map to patient rows across many clinics; the session must
   * be issued for the patient at THIS clinic, otherwise `/me/<path>`
   * rejects the clinic mismatch and the screen never advances. When set,
   * the verified match is chosen by this clinic; if the contact has no
   * patient row here, it's treated as a new patient (onVerifiedWithoutMatch).
   * Left unset on login-only surfaces, which fall back to the first match.
   */
  clinicId?: number | null;
};

/**
 * Email-or-phone + OTP authentication flow used in two places:
 *  1. The authed portal's RootLanding sign-in screen (HealthCom proper).
 *  2. The public booking flow's confirm step (`/clinic-schedule/<path>`)
 *     when the visitor isn't yet signed in.
 *
 * Mirrors the Cognito CUSTOM_WITHOUT_SRP flow:
 *   loginPortal (creates Cognito user if new) → signIn → OTP →
 *   confirmSignIn → onAuthenticated().
 *
 * No DOB step — `loginPortal` doesn't require it. The OTP itself is
 * the security check (proves you own the email/phone).
 */
const EmailOrPhoneLogin: React.FC<Props> = ({
  onAuthenticated,
  onVerifiedWithoutMatch,
  submitLabel = 'Continue',
  verifyLabel = 'Verify',
  clinicId,
}) => {
  const [step, setStep] = useState<'identify' | 'code'>('identify');
  const [mode, setMode] = useState<AuthMode>('phone');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState<CountryCode>('US');
  const [nationalNumber, setNationalNumber] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Holds the OTP challenge id between identify → code submission.
  const [challengeId, setChallengeId] = useState('');
  // When the backend tells us the start was throttled (no SMS / email
  // actually sent), surface a wait message so the user doesn't sit
  // there refreshing their inbox for a code that's not coming.
  const [throttleWaitSeconds, setThrottleWaitSeconds] = useState(0);

  const handleCountryChange = (iso: CountryCode) => {
    setCountry(iso);
    const digits = nationalNumber.replace(/\D/g, '');

    if (digits) {
      setNationalNumber(new AsYouType(iso).input(digits));
    }
  };

  const handleNationalNumberChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    setNationalNumber(new AsYouType(country).input(digits));
  };

  const cognitoUsername = useMemo(() => {
    if (mode === 'email') {
      const v = email.trim().toLowerCase();

      return v && v.includes('@') ? v : null;
    }

    const parsed = parsePhoneNumberFromString(nationalNumber, country);

    return parsed && parsed.isValid() ? parsed.number : null;
  }, [mode, email, nationalNumber, country]);

  const canSubmit = cognitoUsername !== null && !isSubmitting;

  const handleIdentify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!cognitoUsername) {
      setError(
        mode === 'phone'
          ? 'Please enter a valid phone number for the selected country.'
          : 'Please enter a valid email address.'
      );

      return;
    }

    setIsSubmitting(true);

    try {
      // Login-only surfaces (no `onVerifiedWithoutMatch` from the
      // caller) ask the backend to refuse delivery when no Patient
      // row matches, so the UI can stay on the identify step with an
      // honest "we couldn't find an account" message instead of
      // pretending a code was sent.
      const allowNewPatient = !!onVerifiedWithoutMatch;
      const res = await authService.startOtp({
        contact: cognitoUsername,
        contactType: mode === 'email' ? 'email' : 'phone',
        requireAccount: !allowNewPatient,
      });

      if (!allowNewPatient && res.hasMatchingAccount === false) {
        // No account on file and the caller doesn't accept new-patient
        // verification — surface the backend's no-account copy and
        // stay on the identify step so the user can correct the
        // typed contact (vs sit waiting for a code that won't come).
        setError(
          res.message ||
            (mode === 'phone'
              ? "We couldn't find an account with that phone number."
              : "We couldn't find an account with that email.")
        );
        setIsSubmitting(false);

        return;
      }

      if (res.smsOptedOut) {
        // No SMS went out — the phone texted STOP to our number and the
        // carrier blocks delivery. Stay on the identify step and show
        // the backend's opt-back-in copy ("text START to …") instead of
        // advancing to a code screen for a code that won't come.
        setError(res.message);
        setIsSubmitting(false);

        return;
      }

      // Defense in depth: the backend should always return a
      // challengeId on the deliver path, but if something upstream
      // skipped delivery (e.g. unexpected `requireAccount` flag
      // handling) don't advance the UI to a step that needs one.
      if (!res.challengeId) {
        setError(res.message || 'Could not start verification. Please try again.');
        setIsSubmitting(false);

        return;
      }

      setChallengeId(res.challengeId);
      setThrottleWaitSeconds(res.throttled ? (res.retryAfterSeconds ?? 0) : 0);
      setStep('code');
    } catch (err: any) {
      setError(err?.message || 'Login failed. Please try again.');
    }

    setIsSubmitting(false);
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const verifyRes = await authService.verifyOtp({ challengeId, code });

      // The verified contact can map to patient rows across several
      // clinics. In the booking flow we must auth against the patient at
      // the clinic being booked — `matches[0]` is unordered and usually a
      // different clinic, which mints a session whose clinic fails the
      // `/me/<path>` check and leaves this screen stuck. Login-only
      // surfaces don't pass a clinic, so they keep the first match.
      const match =
        clinicId != null
          ? verifyRes.matches?.find(m => m.clinicId === clinicId)
          : verifyRes.matches?.[0];

      if (!match) {
        // No Patient row matches the verified contact at this clinic
        // (either no rows at all, or rows only at other clinics). In the
        // public booking flow this is a normal "new patient here" case —
        // hand control to the caller so it can collect name/DOB and
        // create a pending Patient + appointment in one shot. The portal
        // sign-in path leaves `onVerifiedWithoutMatch` unset, falling
        // through to the original "no account" error.
        if (onVerifiedWithoutMatch) {
          await onVerifiedWithoutMatch({
            contact: cognitoUsername || '',
            contactType: mode,
            challengeId,
          });
          setIsSubmitting(false);

          return;
        }

        setError('No matching account.');
        setIsSubmitting(false);

        return;
      }

      await authService.startSession({
        challengeId,
        patientId: match.patientId,
        clinicId: match.clinicId,
      });
      await onAuthenticated();
    } catch (err: any) {
      setError(err?.message || 'Invalid code. Please try again.');
    }

    setIsSubmitting(false);
  };

  if (step === 'code') {
    const destinationHint = mode === 'phone' ? 'your phone' : 'your email';

    return (
      <form onSubmit={handleVerifyCode} className="flex flex-col gap-4">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {throttleWaitSeconds > 0
              ? `We didn't send another code — please wait about ${throttleWaitSeconds} seconds before requesting a new one. If you already received a code, you can enter it below.`
              : `We sent a verification code to ${destinationHint}.`}
          </p>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-500/10 p-3 ring-1 ring-red-100 dark:ring-red-500/20">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        <div>
          <label
            htmlFor="otp-code"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Verification code
          </label>
          <input
            id="otp-code"
            name="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={e => setCode(e.target.value)}
            className={cn(inputClass, 'mt-1.5 text-center text-lg tracking-widest tabular-nums')}
            placeholder="000000"
            required
            autoFocus
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting || !code}
          className={cn(
            'w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white ring-1',
            isSubmitting || !code
              ? 'bg-gray-300 ring-gray-300 cursor-not-allowed'
              : 'bg-[var(--primary-color)] ring-[var(--primary-color)] hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary-color)]'
          )}
        >
          {isSubmitting ? 'Verifying…' : verifyLabel}
        </button>

        <button
          type="button"
          onClick={() => {
            setCode('');
            setError('');
            setStep('identify');
          }}
          className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Use a different email or phone
        </button>
      </form>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Email / Phone toggle */}
      <div
        role="tablist"
        aria-label="Sign-in method"
        className="grid grid-cols-2 rounded-xl bg-gray-100 dark:bg-gray-950 p-1 text-sm font-medium"
      >
        {(['phone', 'email'] as const).map(m => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => {
              setMode(m);
              setError('');
            }}
            className={cn(
              'rounded-lg py-1.5 transition-colors',
              mode === m
                ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:shadow-none dark:ring-white/10'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            )}
          >
            {m === 'email' ? 'Email' : 'Phone'}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-500/10 p-3 ring-1 ring-red-100 dark:ring-red-500/20">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      <form onSubmit={handleIdentify} className="flex flex-col gap-4">
        {mode === 'email' ? (
          <div>
            <label
              htmlFor="login-email"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Email
            </label>
            <input
              id="login-email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={cn(inputClass, 'mt-1.5')}
              placeholder="you@example.com"
              required
              autoFocus
            />
          </div>
        ) : (
          <div>
            <label
              htmlFor="login-phone"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Phone number
            </label>
            <div className="mt-1.5 flex gap-2">
              <label htmlFor="login-country" className="sr-only">
                Country
              </label>
              <select
                id="login-country"
                name="country"
                value={country}
                onChange={e => handleCountryChange(e.target.value as CountryCode)}
                className="shrink-0 rounded-xl border-0 bg-white dark:bg-gray-950 pl-3 pr-7 py-2.5 text-sm text-gray-900 dark:text-gray-100 ring-1 ring-gray-200 dark:ring-white/10 outline-2 -outline-offset-1 outline-transparent focus:outline-[var(--primary-color)] max-sm:text-base/5 appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 8 5%22 width=%228%22 height=%225%22 fill=%22none%22 stroke=%22%236b7280%22><path d=%22M.5.5 4 4 7.5.5%22/></svg>')] bg-no-repeat bg-[right_0.625rem_center] tabular-nums"
                aria-label="Country"
              >
                {COUNTRY_OPTIONS.map(opt => (
                  <option key={opt.iso} value={opt.iso}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <input
                id="login-phone"
                name="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                value={nationalNumber}
                onChange={e => handleNationalNumberChange(e.target.value)}
                className={cn(inputClass, 'flex-1 min-w-0 tabular-nums')}
                placeholder={new AsYouType(country).input('5551234567')}
                required
                autoFocus
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className={cn(
            'w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white ring-1',
            !canSubmit
              ? 'bg-gray-300 ring-gray-300 cursor-not-allowed'
              : 'bg-[var(--primary-color)] ring-[var(--primary-color)] hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary-color)]'
          )}
        >
          {isSubmitting ? 'Sending…' : submitLabel}
        </button>
      </form>
    </div>
  );
};

export default EmailOrPhoneLogin;
